import React, { useState, useEffect, ReactNode } from "react";
import {
  ModalBody,
  Modal,
  ModalHeader,
  ModalFooter,
  Button,
  Section,
  LabelValuePairs,
  ButtonAdd,
  TextareaField,
  ReactForm,
} from "mta-rkvr-styles";
import { useTranslation } from "react-i18next";
import { MessageDetails } from "../../types/realEstateUnit/RealEstateUnitTypes";
import { get, put } from "../../api/fetch";
import { MessageDetails as MessageDetailsResponse } from "../../api/RealEstateComplex";
import { mapDetailResponseToView } from "./RealEstateComplexMessages";
import { giveCustomCommaValidation, specialSymbolPattern } from "../../util/validationHelper";
import { EditMessageRequest } from "../../api/RealEstateComplex";
import { Input, FieldName } from "../../types/realEstateUnit/messages/MessageTypes";
import { InfobuttonTextsContainer } from "../../types/admin/User";
import { getInfotextInner } from "../../util/generalHelper";
import { getInfoForLabel, getInfoForTitle } from "../../util/TitleWithInfo";

const MessagesDetailModal: React.FC<{
  isOpen: boolean;
  messageId: string;
  close: () => void;
  refreshMessages: () => void;
  infobuttonTexts: InfobuttonTextsContainer;
}> = ({ isOpen, messageId, close: closeModal, refreshMessages, infobuttonTexts }) => {
  const { t } = useTranslation();
  const base = "realEstateUnit.messages.modal.";

  const [data, setData] = useState<MessageDetails>();
  const [isEdit, setEdit] = useState(false);

  useEffect(() => {
    getData();
  }, [messageId]);

  const getData = () => {
    get<MessageDetailsResponse>({
      endPoint: "realEstateUnitMessageDetail",
      pathParams: [messageId],
    }).then(({ body: result }) => {
      mapDetailResponseToView(result).then((dataIn) => {
        setData(dataIn);
      });
    });
  };
  const [message, setMessage] = useState<{
    labels: (ReactNode | string)[];
    values: (ReactNode | ReactNode[] | undefined)[];
  }>();

  const getInfotext = (code: string) => {
    return getInfotextInner(code, "kvyTeatedDetailid", infobuttonTexts);
  };

  const updateFields = () => {
    if (data) {
      const labels: (ReactNode | string)[] = [];
      const values: (ReactNode | ReactNode[] | undefined)[] = [];
      labels.push(getInfoForLabel(t(`${base}${FieldName.receivers}`), getInfotext("saajad")));
      values.push(
        data.saajaIsikud.map((isik) => (
          <>
            {isik.saajaIsikNimi}
            <br />
          </>
        )),
      );

      labels.push(getInfoForLabel(t(`${base}${FieldName.sender}`), getInfotext("saatja")));
      values.push(data.saatjaIsikNimi);

      labels.push(getInfoForLabel(t(`${base}${FieldName.content}`), getInfotext("teateSisu")));
      values.push(data.sisu);

      labels.push(getInfoForLabel(t(`${base}${FieldName.comment}`), getInfotext("kommentaar")));
      values.push(data.kommentaar);

      labels.push(getInfoForLabel(t(`${base}${FieldName.sent}`), getInfotext("saadetud")));
      values.push(data.saadetud);

      labels.push(getInfoForLabel(t(`${base}${FieldName.response}`), getInfotext("vastus")));
      values.push(
        isEdit ? (
          <TextareaField
            name={FieldName.response}
            inlineEdit
            maxLength={2000}
            validations={{
              maxLength: 2000,
              matchRegexp: specialSymbolPattern,
              customCommaValidation: giveCustomCommaValidation(t("common.error.commaEqualsPattern")),
            }}
            validationErrors={{
              maxLength: t("common.error.tooManyLetters"),
              matchRegexp: t("common.error.specialSymbolPattern"),
            }}
          />
        ) : (
          data.vastus
        ),
      );

      labels.push(getInfoForLabel(t(`${base}${FieldName.responseDate}`), getInfotext("vastatud")));
      values.push(data.vastatud || t(`${base}unanswered`).toString());

      labels.push(getInfoForLabel(t(`${base}${FieldName.responder}`), getInfotext("vastaja")));
      values.push(data.vastajaNimi);

      setMessage({
        labels,
        values,
      });
    }
  };

  useEffect(() => {
    updateFields();
  }, [messageId, data, isEdit, infobuttonTexts]);

  const renderModalBody = () => {
    return (
      <Section hideTitle>
        {message && <LabelValuePairs labelSize={5} isSplit={false} labels={message.labels} values={message.values} />}
      </Section>
    );
  };

  const respond = (input: Input) => {
    const body = { kommentaar: input.response };
    put<EditMessageRequest, never>({
      endpoint: "realEstateUnitMessageDetail",
      requestBody: body,
      pathParams: [messageId],
    }).then((res) => {
      if (res.ok) {
        refreshMessages();
        getData();
        setEdit(false);
      }
    });
  };

  const close = () => {
    closeModal();
    setEdit(false);
  };

  return (
    <div>
      <Modal isOpen={isOpen} toggle={closeModal} size="sm">
        <ModalHeader>{getInfoForTitle(t(`${base}message`), getInfotext("pkTeade"))}</ModalHeader>
        <ReactForm onValidSubmit={(input: Input) => respond(input)} onReset={() => setEdit(false)}>
          <ModalBody>{renderModalBody()}</ModalBody>
          <ModalFooter>
            {data &&
              !data.teostatud &&
              (isEdit ? (
                <>
                  <Button type="reset">{t("common.cancel")}</Button>
                  <Button type="submit">{t("common.save")}</Button>
                </>
              ) : (
                <ButtonAdd
                  label={t("common.answer")}
                  onAdd={() => {
                    setEdit(true);
                  }}
                />
              ))}
            <Button color="primary" onClick={close}>
              {t("common.close")}
            </Button>
          </ModalFooter>
        </ReactForm>
      </Modal>
    </div>
  );
};

export default MessagesDetailModal;
